import {
  AHIcon,
  AHIconType,
  AHSeparator,
  Box,
  ListItem,
  useTheme,
  SelectBox,
} from "arkh";
import React, { useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { Switch } from "react-native";
import { WorkspaceTodoEditMainResponseType_TodoWorkspace$key } from "./__generated__/WorkspaceTodoEditMainResponseType_TodoWorkspace.graphql";
import {
  WorkspaceTodoEditMainResponseTypeMutation,
  TodoResponseType,
} from "./__generated__/WorkspaceTodoEditMainResponseTypeMutation.graphql";
import { SetStatusFN } from "./statusCoordinator";

import { FormattedMessage, useIntl } from "react-intl";
import { useInterval } from "widgets/useInterval";

const resultTypes: {
  label: React.ReactNode;
  value: TodoResponseType;
  icon: AHIconType;
}[] = [
  {
    label: (
      <FormattedMessage
        defaultMessage="Image"
        description="Workspace todo edit image response type"
      />
    ),
    value: "IMAGES",
    icon: "camera",
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Number"
        description="Workspace todo edit number response type"
      />
    ),
    value: "NUMBER",
    icon: "number-task",
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Text"
        description="Workspace todo edit text response type"
      />
    ),
    value: "TEXT",
    icon: "text-task",
  },
];
export function WorkspaceTodoEditMainResponseType(props: {
  todo: WorkspaceTodoEditMainResponseType_TodoWorkspace$key;
  setStatus?: SetStatusFN;
  disabled?: boolean;
}) {
  const todo = useFragment(
    graphql`
      fragment WorkspaceTodoEditMainResponseType_TodoWorkspace on TodoWorkspace {
        id
        responseType
        responseDescription
        updatedAtDate {
          ...FormattedDate
          isoString
        }
        updatedBy {
          name
          id
        }
      }
    `,
    props.todo
  );

  const [updateResponseType] =
    useMutation<WorkspaceTodoEditMainResponseTypeMutation>(graphql`
      mutation WorkspaceTodoEditMainResponseTypeMutation(
        $id: ID!
        $responseType: TodoResponseType
      ) @raw_response_type {
        todoWorkspace(id: $id) {
          updateResponseType(newResponseType: $responseType) {
            id
            responseType
            ...WorkspaceTodoEditMainResponseType_TodoWorkspace
          }
        }
      }
    `);

  /*
  const [updateResponseDescription] =
    useMutation<WorkspaceTodoEditMainResponseTypeDescriptionMutation>(graphql`
      mutation WorkspaceTodoEditMainResponseTypeDescriptionMutation(
        $id: ID!
        $responseDescription: String
      ) {
        todoWorkspace(id: $id) {
          updateResponseDescription(
            newResponseDescription: $responseDescription
          ) {
            id
            ...WorkspaceTodoEditMainResponseType_TodoWorkspace
          }
        }
      }
    `);

  const setStatus = props.setStatus;

  const saveDescription = useCallback(
    (responseDescription: string | null) =>
      updateResponseDescription({
        onError: () => setStatus?.(key, "error"),
        onCompleted: () => setStatus?.(key, "saved"),
        variables: {
          id: todo.id,
          responseDescription,
        },
      }),
    [setStatus, todo.id, updateResponseDescription]
  );
*/
  const [pending, setPending] = useState(false);

  const theme = useTheme();
  const colorProps = {
    activeThumbColor: theme.primary,
    activeTrackColor: theme["track-background"],
    trackColor: theme["border-strong"],
  } as any;
  const intl = useIntl();

  const placehodler1 = intl.formatMessage({
    defaultMessage: `E.g. "Photograph the new signs in the store."`,
    description: "Workspace todo edit response description placeholder1",
  });

  const placehodler2 = intl.formatMessage({
    defaultMessage: `E.g. "Count the number of removed products."`,
    description: "Workspace todo edit response description placeholder2",
  });

  const placehodler3 = intl.formatMessage({
    defaultMessage: `E.g. "Describe your first impression at the entrance."`,
    description: "Workspace todo edit response description placeholder3",
  });

  const placeholders = [placehodler1, placehodler2, placehodler3];

  const [, setI] = useState(0);
  useInterval(() => setI((p) => (p + 1) % placeholders.length), 3000);

  return (
    <Box
      cornerRadius="large"
      color={props.disabled ? "transparent" : "surface-primary"}
    >
      <ListItem
        disabled
        color={props.disabled ? "transparent" : "surface-primary"}
        border="border-primary"
        left={<AHIcon type="todo-response" color="darkText" />}
        right={
          <Switch
            {...colorProps}
            style={{ opacity: props.disabled ? 0.4 : 1 }}
            disabled={props.disabled}
            value={pending || !!todo.responseType}
            onValueChange={(val) => {
              if (val) {
                setPending(true);
                updateResponseType({
                  variables: {
                    id: todo.id,
                    responseType: resultTypes[0].value,
                  },
                });
              } else {
                updateResponseType({
                  variables: {
                    id: todo.id,
                    responseType: null,
                  },
                });
                setPending(false);
              }
            }}
          />
        }
        isFirst
        isLast={!todo.responseType}
      >
        <ListItem.TitleSecondary>
          <FormattedMessage
            defaultMessage="Required response"
            description="Workspace todo edit required response title"
          />
        </ListItem.TitleSecondary>
      </ListItem>
      {pending || !!todo.responseType ? (
        <>
          <AHSeparator />
          <Box style={{ flexDirection: "column" }}>
            <Box style={{ flexDirection: "row" }} spaceHorizontal="medium">
              {resultTypes.map((resultType) => (
                <SelectBox
                  key={resultType.value}
                  disabled={props.disabled}
                  isSelected={todo.responseType === resultType.value}
                  icon={resultType.icon}
                  onPress={() => {
                    updateResponseType({
                      optimisticResponse: {
                        todoWorkspace: {
                          updateResponseType: {
                            id: todo.id,
                            responseType: resultType.value,
                            responseDescription: todo.responseDescription,
                            updatedAtDate: todo.updatedAtDate
                              ? {
                                  ...todo.updatedAtDate,
                                  __isDate: "UnzonedDate" as const,
                                }
                              : null,

                            updatedBy: todo.updatedBy,
                          },
                        },
                      },
                      variables: {
                        id: todo.id,
                        responseType: resultType.value,
                      },
                    });
                  }}
                >
                  {resultType.label}
                </SelectBox>
              ))}
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
